import { ApiError, HttpClient } from "@utils/httpClient";
import type { MappingServiceViewModel, VectorIndexProfile } from "@models/service";
import type {
  CreateJobRequest,
  CreateJobResponse,
  CreateJobResponseViewModel,
  JobListResponse,
  JobListViewModel,
  JobStatus,
  JobStatusResponse,
  JobViewModel,
  ItemMpMatchViewModel,
  ItemMpSearchResponse,
  ItemRequestPreviewRequest,
  ItemRequestPreviewResponse,
  SendItemRequestRequest,
  SendItemRequestResponse,
  WdTaxonomyResponse,
  WdTaxonomyViewModel,
  SearchedMatch,
  TaxonomyGroupResponse,
  TaxonomyGroupViewModel,
  ResultExportResponse,
  ResultExportViewModel,
  RowExamplesResponse,
  SimilarHistoryEntry,
  TemplateItemsResponse,
  TemplateItemViewModel,
  InputTemplateResponse,
} from "@models/job";
import { MappingResultKind } from "@models/mappingResult";
import type {
  MappingResult,
  MappingResultResponseWire,
  MappingResultRow,
  MappingResultRowWire,
  RowAlternatives,
  RowAlternativesResponseWire,
} from "@models/mappingResult";
import {
  buildMappingResult,
  buildMappingResultRow,
  buildRowAlternatives,
} from "@utils/builders/mappingResult";
import type {
  PatchRequestViewModel,
  PatchResultResponse,
  PatchResultViewModel,
  RevisionListResponse,
  RevisionListViewModel,
} from "@models/revision";
import {
  buildCreateJobResponseViewModel,
  buildJobListViewModel,
  buildResultExportViewModel,
  buildJobViewModelFromStatus,
} from "@utils/builders/job";
import {
  buildPatchRequest,
  buildPatchResultViewModel,
  buildRevisionListViewModel,
} from "@utils/builders/revision";
import { collapseToServices } from "@utils/builders/service";

const BASE_URL = import.meta.env.VITE_MAPPING_API_BASE_URL;

if (!BASE_URL) {
  // Surface mis-configuration loudly during build/dev rather than
  // silently issuing requests to `undefined/...`.
  console.warn(
    "VITE_MAPPING_API_BASE_URL is not set. The mapping dashboard " +
      "will fail to fetch services until it is configured in .env.",
  );
}

export class MappingApi {
  private httpClient: HttpClient;

  constructor(httpClient?: HttpClient) {
    this.httpClient = httpClient ?? new HttpClient(BASE_URL ?? "");
  }

  /**
   * Fetch the list of available mapping services (one card per
   * service). Returns the collapsed view-model list — callers do
   * not see the raw `(plugin, model)` rows.
   */
  async listServices(): Promise<MappingServiceViewModel[]> {
    const profiles = await this.httpClient.makeAuthenticatedRequest<
      VectorIndexProfile[]
    >("/plugins", { method: "GET" });
    return collapseToServices(profiles ?? []);
  }

  /**
   * Create a job + receive a presigned PUT for the file upload.
   * The pipeline starts when the file is PUT to `uploadUrl`.
   */
  async createJob(
    request: CreateJobRequest,
  ): Promise<CreateJobResponseViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<CreateJobResponse>(
        "/jobs",
        {
          method: "POST",
          body: JSON.stringify(request),
        },
      );
    return buildCreateJobResponseViewModel(response);
  }

  /**
   * PUT the file bytes to the presigned upload URL returned by `createJob`.
   * Content-Type must match `uploadContentType` from the create response
   * (S3 rejects mismatches because the presign is signed against it).
   *
   * Uses XMLHttpRequest so callers can subscribe to upload progress via
   * `onProgress(percent)`. `fetch` cannot stream upload progress in most
   * browsers as of 2026, so the swap is intentional.
   */
  async uploadFileToS3(
    uploadUrl: string,
    file: File | Blob,
    contentType: string,
    onProgress?: (percent: number) => void,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Type", contentType);

      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable) return;
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(Math.min(100, Math.max(0, percent)));
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(100);
          resolve();
        } else {
          // Stable, semantic code so the UI can render a friendly message
          // per failure class (expired presign, oversize, server fault).
          const code =
            xhr.status === 403
              ? "upload_expired"
              : xhr.status === 413
                ? "upload_too_large"
                : xhr.status >= 500
                  ? "upload_server_error"
                  : "upload_http_error";
          reject(
            new ApiError(
              `Upload failed: ${xhr.status} ${xhr.statusText}`,
              xhr.status,
              code,
            ),
          );
        }
      };
      xhr.onerror = () => {
        reject(new ApiError("Upload network error", 0, "upload_network"));
      };
      xhr.onabort = () => {
        reject(new ApiError("Upload aborted", 0, "upload_aborted"));
      };
      xhr.send(file);
    });
  }

  /**
   * Paginated history of this org's jobs (newest first). Optional
   * `service` / `pluginId` / `status` filters scope the page server-side,
   * plus `userId` (job owner) and a `startDate` / `endDate` (`YYYY-MM-DD`)
   * creation window. Backend caps `limit` at 200; default is 50.
   */
  async listJobs(
    options: {
      cursor?: string | null;
      limit?: number;
      service?: string;
      pluginId?: string;
      status?: JobStatus;
      userId?: string | null;
      startDate?: string | null;
      endDate?: string | null;
    } = {},
  ): Promise<JobListViewModel> {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set("limit", String(options.limit));
    if (options.cursor) params.set("cursor", options.cursor);
    if (options.service) params.set("service", options.service);
    if (options.pluginId) params.set("plugin_id", options.pluginId);
    if (options.status) params.set("status", options.status);
    if (options.userId) params.set("user_id", options.userId);
    if (options.startDate) params.set("start_date", options.startDate);
    if (options.endDate) params.set("end_date", options.endDate);
    const qs = params.toString();
    const path = `/jobs${qs ? `?${qs}` : ""}`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<JobListResponse>(path, {
        method: "GET",
      });
    return buildJobListViewModel(response);
  }

  /**
   * Permanently delete a job, its per-row results, and S3 artifacts.
   * Owner-only on the backend: a 403 means the caller did not create the
   * job, a 409 means it is still processing. Resolves on 204.
   */
  async deleteJob(jobId: string): Promise<void> {
    await this.httpClient.makeAuthenticatedRequest<void>(
      `/jobs/${encodeURIComponent(jobId)}`,
      { method: "DELETE" },
    );
  }

  async getJob(jobId: string): Promise<JobViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<JobStatusResponse>(
        `/jobs/${encodeURIComponent(jobId)}`,
        { method: "GET" },
      );
    return buildJobViewModelFromStatus(response);
  }

  /**
   * Paginated per-row results. Returns 409 if the job hasn't reached
   * `Running` yet — callers should treat that as "not ready, retry later".
   */
  async getJobResult(
    jobId: string,
    options: { cursor?: string | null; limit?: number } = {},
  ): Promise<MappingResult> {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set("limit", String(options.limit));
    if (options.cursor) params.set("cursor", options.cursor);
    const qs = params.toString();
    const path = `/jobs/${encodeURIComponent(jobId)}/result${qs ? `?${qs}` : ""}`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<MappingResultResponseWire>(
        path,
        { method: "GET" },
      );
    return buildMappingResult(response);
  }

  /**
   * Every candidate one row could point at, fetched when the reviewer expands
   * the dropdown. The result list ships only `hasAlternative`, so these load
   * lazily and are cached client-side.
   *
   * `kind` is a parameter because the response carries none: one request is
   * one row, and the caller opened the dropdown on that row. Rank 1 — the
   * pipeline's own top match — is in the list, which is what makes a
   * correction undoable.
   */
  async getRowAlternatives(
    jobId: string,
    rowIndex: number,
    kind: MappingResultKind,
  ): Promise<RowAlternatives> {
    const path =
      `/jobs/${encodeURIComponent(jobId)}/results/${rowIndex}/alternatives`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<RowAlternativesResponseWire>(
        path,
        { method: "GET" },
      );
    return buildRowAlternatives(response, kind);
  }

  /**
   * Fetch the golden "accepted a similar example before" history for one
   * match of a row on demand (when the reviewer opens the score info-popup).
   * `rank` defaults server-side to the row's effective (top / chosen) match.
   */
  async getRowExamples(
    jobId: string,
    rowIndex: number,
    rank?: number,
  ): Promise<SimilarHistoryEntry[]> {
    const params = new URLSearchParams();
    if (rank !== undefined) params.set("rank", String(rank));
    const qs = params.toString();
    const path =
      `/jobs/${encodeURIComponent(jobId)}/results/${rowIndex}/examples` +
      (qs ? `?${qs}` : "");
    const response =
      await this.httpClient.makeAuthenticatedRequest<RowExamplesResponse>(path, {
        method: "GET",
      });
    return response.examples ?? [];
  }

  /**
   * The empty OE input workbook a reviewer fills in and uploads.
   *
   * Base64 in, Blob out. The bytes are not stored anywhere — the file is
   * identical for every caller — so there is no presigned URL to hand back the
   * way the result export does.
   */
  async getOeInputTemplate(): Promise<{ blob: Blob; filename: string }> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<InputTemplateResponse>(
        "/plugins/oe/input-template",
        { method: "GET" },
      );
    const bytes = Uint8Array.from(atob(response.content_base64), (c) =>
      c.charCodeAt(0),
    );
    return {
      blob: new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      filename: response.filename,
    };
  }

  /**
   * Build the .xlsx of all result rows server-side and return a presigned
   * download URL. `downloadUrl` is null when the job has no rows.
   */
  async exportJobResult(jobId: string): Promise<ResultExportViewModel> {
    const path = `/jobs/${encodeURIComponent(jobId)}/result/export`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<ResultExportResponse>(path, {
        method: "GET",
      });
    return buildResultExportViewModel(response);
  }

  /**
   * Build — but do NOT send — the mail asking whoever maintains the item_mp
   * catalogue to create the entries this job is missing. Returns the resolved
   * recipients plus an EDITABLE subject/body, and the same content structured
   * as `lines` so a table can be rendered instead of parsing the text.
   *
   * The preview step is the point: `suggested_item_mp_text` is what the select
   * LLM proposed, not a fact, so the reviewer rewords it — or drops rows —
   * before a colleague reads it. Pass the response's `row_indexes` back to
   * {@link sendItemRequest} so what is sent and what is counted agree.
   *
   * Rows a reviewer has already resolved (corrected onto a real candidate, or
   * rejected) are excluded server-side, so this is never a request to create
   * something the reviewer just found. Returns 409 before the job has written
   * any row, and 400 when `to` falls outside the deployment's allowed domains.
   */
  async previewItemRequest(
    jobId: string,
    body: ItemRequestPreviewRequest = {},
  ): Promise<ItemRequestPreviewResponse> {
    const path = `/jobs/${encodeURIComponent(jobId)}/item-requests/preview`;
    return this.httpClient.makeAuthenticatedRequest<ItemRequestPreviewResponse>(
      path,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  /**
   * Send the item request by email.
   *
   * `subject` / `body` are sent VERBATIM when supplied — that is the text the
   * reviewer edited in the preview; omit both and the server builds the same
   * message the preview returned.
   *
   * Errors worth distinguishing in the UI: 409 `no_item_request_rows` (nothing
   * to ask for), 400 `mail_recipient_not_allowed` (address outside the allowed
   * domains), 503 `mail_not_configured` (this environment has no verified SES
   * sender — the feature is off here, not the caller's mistake), and 502
   * `mail_send_failed` (the provider rejected it).
   */
  async sendItemRequest(
    jobId: string,
    body: SendItemRequestRequest,
  ): Promise<SendItemRequestResponse> {
    const path = `/jobs/${encodeURIComponent(jobId)}/item-requests`;
    return this.httpClient.makeAuthenticatedRequest<SendItemRequestResponse>(
      path,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  /**
   * Apply a reviewer edit to one result row. The server writes the new
   * state in place on `mapping-results` AND appends a revision to the
   * audit log, returning both so the client can render without a
   * follow-up GET.
   */
  async patchResult(
    jobId: string,
    rowIndex: number,
    payload: PatchRequestViewModel,
  ): Promise<PatchResultViewModel> {
    const path = `/jobs/${encodeURIComponent(jobId)}/results/${rowIndex}`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<PatchResultResponse>(path, {
        method: "PATCH",
        body: JSON.stringify(buildPatchRequest(payload)),
      });
    return buildPatchResultViewModel(response);
  }

  /**
   * QA-template targets a reviewer may pick when adding a manual row.
   * Empty when the plugin has no template configured.
   */
  async listTemplateItems(jobId: string): Promise<TemplateItemViewModel[]> {
    const path = `/jobs/${encodeURIComponent(jobId)}/template-items`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<TemplateItemsResponse>(
        path,
        { method: "GET" },
      );
    return response.items.map((it) => ({
      itemMpId: it.item_mp_id,
      itemMpListing: it.item_mp_listing,
      category: it.category,
    }));
  }

  /**
   * The plugin's full item_mp catalogue (from the vector index manifest),
   * filtered by substring. Spans every field, not just the QA template.
   */
  async searchItemMp(
    jobId: string,
    query: string,
    limit = 20,
  ): Promise<ItemMpMatchViewModel[]> {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    const path = `/jobs/${encodeURIComponent(jobId)}/item-mp?${params.toString()}`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<ItemMpSearchResponse>(path, {
        method: "GET",
      });
    return response.items.map((it) => ({
      itemMpId: it.item_mp_id,
      itemMpText: it.item_mp_text,
    }));
  }

  /**
   * Append a manual row, `pending`, no mapping run. With `itemMpText` the row
   * carries the `matches` the caller retrieved from the search service (best
   * first); without it the row is synthesized from the QA template (empty RMI
   * values / score). Returns the created row.
   */
  async addResultRow(
    jobId: string,
    payload: {
      itemMpId: number;
      value: string;
      itemMpText?: string;
      matches?: SearchedMatch[];
      degraded?: string[];
    },
  ): Promise<MappingResultRow> {
    const path = `/jobs/${encodeURIComponent(jobId)}/results`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<{
        row: MappingResultRowWire;
      }>(path, {
        method: "POST",
        body: JSON.stringify({
          item_mp_id: payload.itemMpId,
          value: payload.value,
          ...(payload.itemMpText ? { item_mp_text: payload.itemMpText } : {}),
          ...(payload.matches?.length ? { matches: payload.matches } : {}),
          ...(payload.degraded?.length ? { degraded: payload.degraded } : {}),
        }),
      });
    return buildMappingResultRow(response.row);
  }

  /**
   * The wd group taxonomy for the job's index — the "add row" filters.
   *
   * Comes from the index manifest because that is the only place the id → name
   * map lives: a vector stores just `main_group_id` / `sub_group_id`. Filter a
   * search on the `id`, show the `name`. Empty when the plugin has no taxonomy,
   * in which case the caller renders no filters rather than erroring.
   */
  async getTaxonomy(jobId: string): Promise<WdTaxonomyViewModel> {
    const path = `/jobs/${encodeURIComponent(jobId)}/taxonomy`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<WdTaxonomyResponse>(path, {
        method: "GET",
      });
    const toGroup = (g: TaxonomyGroupResponse): TaxonomyGroupViewModel => ({
      id: g.id,
      name: g.name,
      mainGroupId: g.main_group_id ?? null,
    });
    return {
      mainGroups: response.main_groups.map(toGroup),
      subGroups: response.sub_groups.map(toGroup),
    };
  }

  /**
   * Paginated history of edits applied to one result row, newest first.
   */
  async listRevisions(
    jobId: string,
    rowIndex: number,
    options: { cursor?: string | null; limit?: number } = {},
  ): Promise<RevisionListViewModel> {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set("limit", String(options.limit));
    if (options.cursor) params.set("cursor", options.cursor);
    const qs = params.toString();
    const path =
      `/jobs/${encodeURIComponent(jobId)}/results/${rowIndex}/revisions` +
      (qs ? `?${qs}` : "");
    const response =
      await this.httpClient.makeAuthenticatedRequest<RevisionListResponse>(
        path,
        {
          method: "GET",
        },
      );
    return buildRevisionListViewModel(response);
  }

  /**
   * Roll a result row back to a prior revision. The server copies the
   * named revision's payload over the current state and appends a new
   * revision flagged `source=restore` (so the restore itself is
   * auditable and undoable).
   */
  async restoreRevision(
    jobId: string,
    rowIndex: number,
    revisionId: string,
  ): Promise<PatchResultViewModel> {
    const path =
      `/jobs/${encodeURIComponent(jobId)}/results/${rowIndex}` +
      `/revisions/${encodeURIComponent(revisionId)}/restore`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<PatchResultResponse>(path, {
        method: "POST",
        body: "{}",
      });
    return buildPatchResultViewModel(response);
  }
}

export const mappingApi = new MappingApi();
